import { Component, computed, inject, PLATFORM_ID, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiService } from '../../api.service';
import { TAGSongRequest } from '../../../models/tagsongrequest';
import { MessageService } from 'primeng/api';
import { isPlatformBrowser } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { IconFieldModule } from 'primeng/iconfield';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputIconModule } from 'primeng/inputicon';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FluidModule } from 'primeng/fluid';
import { ButtonModule } from 'primeng/button';
import { TimeSpanPipe } from '../../timespan.pipe';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-song-request-form',
  imports: [
    TranslatePipe,
    ProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule,
    IconFieldModule,
    InputIconModule,
    MatIcon,
    InputTextModule,
    FloatLabelModule,
    FluidModule,
    ButtonModule,
    SkeletonModule
  ],
  providers: [DialogService, TranslateService, TimeSpanPipe],
  templateUrl: './song-request-form.html',
  styleUrl: './song-request-form.scss',
})
export class SongRequestForm {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  //private router = inject(Router);
  //private route = inject(ActivatedRoute);
  //private socket = inject(Socket);
  public api = inject(ApiService);
  //private sanitzer = inject(DomSanitizer);
  private platformId = inject(PLATFORM_ID);
  public timespan = inject(TimeSpanPipe);

  loadingPage = signal(true);

  songRequests = signal<TAGSongRequest[]>([]);
  loadingRequests = signal(true);

  searchFilter = signal('');
  filteredOngoingRequests = computed(() => {
    let all = this.songRequests().filter((sr) => !sr.played);

    if (this.searchFilter().trim() == '') {
      return all;
    }

    return all.filter((t) => {
      return (
        t.title.toLowerCase().includes(this.searchFilter().toLowerCase()) ||
        t.artist.toLowerCase().includes(this.searchFilter().toLowerCase())
      );
    });
  });
  filteredPlayedRequests = computed(() => {
    let all = this.songRequests().filter((sr) => sr.played);

    if (this.searchFilter().trim() == '') {
      return all;
    }

    return all.filter((t) => {
      return (
        t.title.toLowerCase().includes(this.searchFilter().toLowerCase()) ||
        t.artist.toLowerCase().includes(this.searchFilter().toLowerCase())
      );
    });
  });

  requestTimer: any = undefined;
  remainingTimeRequest = signal(0);
  submittingRequest = signal(false);
  voteTimer: any = undefined;
  remainingTimeVote = signal(0);
  submittingVote = signal(false);

  reloadTimer: any = undefined;

  requestForm = new FormGroup({
    artist: new FormControl('', []),
    title: new FormControl('', [Validators.required]),
  });

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      console.log(this.api.config().enableSongRequests);
      if (!this.api.config().enableSongRequests) {
        this.loadingPage.set(false);
        return;
      }

      if (isPlatformBrowser(this.platformId)) {
        // TODO: Replace with API call
        if (localStorage.getItem('lastRequest') != null) {
          let lastRequest = new Date(localStorage.getItem('lastRequest'));
          let timeDiff = (new Date().getTime() - lastRequest.getTime()) / 1000;
          this.remainingTimeRequest.set(Math.max(0, this.api.config().songRequestTimeouts - timeDiff));
          console.log(this.api.config().songRequestTimeouts, timeDiff);
          if (this.remainingTimeRequest() > 0) this.startRequestTimer();
        }

        if (localStorage.getItem('lastVote') != null) {
          let lastVote = new Date(localStorage.getItem('lastVote'));
          let timeDiff = (new Date().getTime() - lastVote.getTime()) / 1000;
          this.remainingTimeVote.set(Math.max(0, this.api.config().songRequestTimeouts - timeDiff));

          if (this.remainingTimeVote() > 0) this.startVoteTimer();
        }
      }

      this.loadSongRequests();

      if (isPlatformBrowser(this.platformId)) {
        this.reloadTimer = setInterval(() => {
          this.loadSongRequests();
        }, 5000);
      }
    });
  }

  startRequestTimer() {
    this.requestTimer = setInterval(() => {
      this.remainingTimeRequest.update((s) => Math.max(0, s - 1));
      if (this.remainingTimeRequest() <= 0) {
        clearInterval(this.requestTimer);
        this.requestTimer = undefined;
      }
    }, 1000);
  }

  startVoteTimer() {
    this.voteTimer = setInterval(() => {
      this.remainingTimeVote.update((s) => Math.max(0, s - 1));
      if (this.remainingTimeVote() <= 0) {
        clearInterval(this.voteTimer);
        this.voteTimer = undefined;
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.reloadTimer) clearInterval(this.reloadTimer);

    if (this.requestTimer) clearInterval(this.requestTimer);
    if (this.voteTimer) clearInterval(this.voteTimer);
  }

  loadSongRequests() {
    this.loadingPage.set(false);

    console.log("Loaded requests: ", this.songRequests());

    this.api
      .getSongRequests()
      .then((songRequests: TAGSongRequest[]) => {
        songRequests.sort((a, b) => a.votes - b.votes);
        this.songRequests.set(songRequests);
        this.loadingRequests.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadSongRequests')(),
          detail: reason.message,
        });
      });
  }

  sendRequest() {
    if (!this.api.config().enableSongRequests) return;
    if (this.remainingTimeRequest() > 0) return;

    let songRequest = new TAGSongRequest(this.requestForm.value);

    if (this.api.isLoggedIn()) {
      songRequest.owner = this.api.user().username;
    }

    // Check similarity with other wishes
    let similar: TAGSongRequest[] = this.songRequests().filter((sr) => {
      return (
        !sr.played &&
        this.levenshteinDistance(sr.title, songRequest.title) < 4 &&
        this.levenshteinDistance(sr.artist, songRequest.artist) < 4
      );
    });

    if (similar.length > 0) {
      ActionConfirmationDialog.openDialog(
        this.dialogService,
        new ActionConfirmationInfo(
          this.translate.translate('songrequests.similar.title', {
            request: songRequest,
            similar: similar[0],
          })(),
          this.translate.translate('songrequests.similar.message', {
            request: songRequest,
            similar: similar[0],
          })(),
          ActionConfirmationInfo.yesno_nprim,
        ),
      ).then((action: string) => {
        if (action === 'yes') {
          this.submitRequest(songRequest);
        } else {
          this.requestForm.reset();
        }
      });
    } else {
      this.submitRequest(songRequest);
    }
  }

  submitRequest(songRequest: TAGSongRequest) {
    this.submittingRequest.set(true);

    this.api
      .newSongRequest(songRequest)
      .then((result) => {
        this.remainingTimeRequest.set(this.api.config().songRequestTimeouts);
        this.startRequestTimer();
        localStorage.setItem('lastRequest', new Date().toISOString());

        this.loadSongRequests();
        this.requestForm.reset();
        this.submittingRequest.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotProcessSongRequest')(),
          detail: reason.message,
        });
        this.submittingRequest.set(false);
      });
  }

  vote(songRequestId: string) {
    if (!this.api.config().enableSongRequests) return;
    if (this.remainingTimeVote() > 0) return;

    this.submittingVote.set(true);

    this.api
      .voteForSongRequest(songRequestId)
      .then((result) => {
        this.remainingTimeVote.set(this.api.config().songRequestTimeouts);
        this.startVoteTimer();
        localStorage.setItem('lastVote', new Date().toISOString());

        this.loadSongRequests();
        this.submittingVote.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotProcessSongVote')(),
          detail: reason.message,
        });
        this.submittingVote.set(false);
      });
  }

  /**
   * Copied from https://masoudx.medium.com/sorting-words-by-similarity-in-typescript-a-guide-to-use-levenshtein-distance-algorithm-f6b4f3b57008
   */
  levenshteinDistance(a: string, b: string): number {
    // Create a 2D array to store the distances
    let distances = new Array(a.length + 1);
    for (let i = 0; i <= a.length; i++) {
      distances[i] = new Array(b.length + 1);
    }

    // Initialize the first row and column
    for (let i = 0; i <= a.length; i++) {
      distances[i][0] = i;
    }
    for (let j = 0; j <= b.length; j++) {
      distances[0][j] = j;
    }

    // Fill in the rest of the array
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        if (a[i - 1] === b[j - 1]) {
          distances[i][j] = distances[i - 1][j - 1];
        } else {
          distances[i][j] =
            Math.min(distances[i - 1][j], distances[i][j - 1], distances[i - 1][j - 1]) + 1;
        }
      }
    }

    // Return the final distance
    return distances[a.length][b.length];
  }
}
